import SVGIcon from 'components/SVGIcon/SVGIcon';
import useProductUomInfo, { SPECIAL_UOM } from 'hooks/useProductUomInfo';
import React, { FC, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Mousewheel } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Language, Product } from 'types/schema';
import calculateVAT from 'utils/calculateVAT';
import formatMoney from 'utils/formatMoney';
import media from 'utils/mediaStyle';
import utilMessages from 'utils/messages';
import { EventName, sendGA4Event } from 'utils/triggerGA4Event';
import generateProductSlug from '../../../utils/generateProductSlug';
import chevronLeftIcon from '../_icons/chevronLeft.svg';
import chevronRightIcon from '../_icons/chevronRight.svg';
import DiscountIcon from '../_icons/discountIcon.svg';
import { formatUnitPrice } from '../ProductPrice';

const Container = styled.div`
  position: relative;
  margin: 16px 0 0 0 !important;

  ${media.lg`
    margin: 20px 0 0 0 !important;
  `}
`;
const SwiperElement = styled(Swiper)`
  .swiper-button-next,
  .swiper-button-prev {
    display: none !important;
  }
`;

const SwiperSlideElement = styled(SwiperSlide)`
  width: fit-content !important;
`;

const SuggestBuyByBoxItemName = styled.div`
  font-size: 16px;
  font-weight: 500;
  line-height: 18px;
  color: #242527;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SuggestBuyByBoxItem = styled.div`
  display: flex;
  min-width: 180px;
  height: 88px;
  padding: 6px 12px;
  flex-direction: column;
  background: #ffffff;
  border-right: 1px solid #d5d7d5;
  border-top: 1px solid #d5d7d5;
  border-bottom: 1px solid #d5d7d5;

  ${({ isFirstChild }) =>
    isFirstChild &&
    `
    border-left: 1px solid #d5d7d5;
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
    `}
  ${({ isLastChild }) =>
    isLastChild &&
    `
    border-bottom-right-radius: 8px;
    border-top-right-radius: 8px;
    `}
  


  ${({ isSelected }) =>
    isSelected &&
    `
    border-color: #43a047;
    background: #f0f9f1;
    border-left: 1px solid #43a047;

    ${SuggestBuyByBoxItemName} {
      color: #43a047;
    }
  `}

  ${({ isPrevSelected }) =>
    isPrevSelected &&
    `
    border-right: none;
  `}

  &:hover {
    border-color: #43a047;
    background: #f0f9f1;

    ${SuggestBuyByBoxItemName} {
      color: #43a047;
    }
  }
`;

const SuggestBuyByBoxItemPrice = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: #343538;
  margin-top: 4px;

  span {
    color: #757d73;
  }
`;

const ExclusiveTag = styled.div`
  margin-top: 4px;
  width: fit-content;

  height: 24px;
  border-radius: 4px;
  background: #ffffff;
  border: 1px solid #d5d7d5;

  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: #343538;

  padding: 3px 8px 5px;
`;

const SuggestBuyByBoxItemDiscountContainer = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
  padding: 2px 4px;
  gap: 4px;
  border-radius: 4px;
  background: #ffecdf;
  width: fit-content;
`;

const SuggestBuyByBoxItemDiscountPrice = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: #e56910;

  &:last-child {
    font-weight: 500;
  }

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LeftIconContainer = styled.div`
  position: absolute;
  top: 30px;
  background: #fffff;
  left: -15px;
  width: 30px;
  height: 30px;
  background: #ffffff;
  padding: 8px 12px;
  cursor: pointer;
  border: 1px solid #d5d7d5;
  border-radius: 50%;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f0f9f1;
    transition: background 0.2s;
  }
`;

const RightIconContainer = styled.div`
  position: absolute;
  top: 30px;
  background: #ffffff;
  right: -15px;
  width: 30px;
  height: 30px;
  background: #fffff;
  padding: 8px 12px;
  cursor: pointer;
  border: 1px solid #d5d7d5;
  border-radius: 50%;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f0f9f1;
    transition: background 0.2s;
  }
`;

const SuggestBuyByBoxListItem: FC<{
  product: Product;
  isFirstChild: boolean;
  isLastChild: boolean;
  isSelected: boolean;
  isPrevSelected: boolean;
}> = ({ product, isFirstChild, isLastChild, isSelected, isPrevSelected }) => {
  const lang = localStorage.getItem('lang') || Language.Vi;
  const { baseUom: currBaseUom, netUom: currNetUom, uom: currUom } = useProductUomInfo(product);

  const renderUomPrice = useMemo(() => {
    if (product?.baseUomPrice && product?.netUomPrice && product?.baseUnit !== 1) {
      return `(${formatUnitPrice(product?.baseUomPrice, currBaseUom)})`;
    }
    if (product?.baseUomPrice && product?.baseUnit !== 1) {
      return `(${formatUnitPrice(product?.baseUomPrice, currBaseUom)})`;
    }
    if (product?.netUomPrice && product?.netUnit !== 1) {
      return `(${formatUnitPrice(product?.netUomPrice, currNetUom)})`;
    }
    return '';
  }, [product?.baseUomPrice, product?.netUomPrice, product?.baseUnit, product?.netUnit, currBaseUom, currNetUom]);

  const isSpecialUom = useMemo(() => {
    if (currNetUom === SPECIAL_UOM[0] && currUom === SPECIAL_UOM[0]) return true;

    if (SPECIAL_UOM.length > 1 && SPECIAL_UOM[1].includes('L') && currNetUom?.includes('L')) {
      if (lang === Language.Vi && currUom?.includes('Lít')) return true;
      if (lang === Language.En && currUom?.includes('Liter')) return true;
    }

    return false;
  }, [currNetUom, currUom, lang, SPECIAL_UOM]);

  const boxName = useMemo(() => {
    if (!currUom) return '';

    const isSameBaseUom = currBaseUom.toLowerCase() === currUom.toLowerCase();

    if (product?.baseUnit && currBaseUom && product?.netUnit && currNetUom) {
      if (isSameBaseUom && product?.baseUnit === 1) {
        return isSpecialUom ? `1 ${currUom}` : `1 ${currUom} ${product?.netUnit}${currNetUom.toLowerCase()}`;
      }

      return `${!isSameBaseUom ? `1 ${currUom}` : ''} ${product?.baseUnit} ${currBaseUom} x ${product?.netUnit}${currNetUom.toLowerCase()}`;
    }
    if (product?.baseUnit && currBaseUom) {
      return `${!isSameBaseUom ? `1 ${currUom}` : ''} ${product?.baseUnit} ${currBaseUom}`;
    }
    if (product?.netUnit && currNetUom) {
      if (isSpecialUom && product?.netUnit === 1) return `1 ${currUom}`;

      return `${!isSameBaseUom ? `1 ${currUom}` : ''} ${product?.netUnit}${currNetUom.toLowerCase()}`;
    }

    return `1 ${currUom}`;
  }, [product?.baseUnit, product?.netUnit, currBaseUom, currNetUom, currUom, isSpecialUom]);

  return (
    <SuggestBuyByBoxItem
      isFirstChild={isFirstChild}
      isLastChild={isLastChild}
      isSelected={isSelected}
      isPrevSelected={isPrevSelected}
    >
      <SuggestBuyByBoxItemName>{boxName}</SuggestBuyByBoxItemName>

      {product?.private ? (
        <ExclusiveTag>
          <FormattedMessage {...utilMessages.exclusive} />
        </ExclusiveTag>
      ) : (
        <>
          <SuggestBuyByBoxItemPrice>
            {formatMoney(
              calculateVAT(product?.priceV2?.price || 0, product?.priceV2?.vat || 0, !!product?.priceV2?.vatIncluded)
                .beforeVat,
            )}
            đ<span>{` ${renderUomPrice}`}</span>
          </SuggestBuyByBoxItemPrice>

          {!!product?.savingAmount && (
            <SuggestBuyByBoxItemDiscountContainer>
              <SVGIcon src={DiscountIcon} />
              <SuggestBuyByBoxItemDiscountPrice>
                <FormattedMessage {...utilMessages.saveAmount} />
                <span>{` ${formatMoney(product?.savingAmount)}đ`}</span>
              </SuggestBuyByBoxItemDiscountPrice>
            </SuggestBuyByBoxItemDiscountContainer>
          )}
        </>
      )}
    </SuggestBuyByBoxItem>
  );
};

const SuggestBuyByBoxList: FC<{ suggestBuyProducts: Product[]; currentProduct: Product }> = ({
  suggestBuyProducts,
  currentProduct,
}) => {
  const lang = localStorage.getItem('lang') || 'vi';

  const swiperRef = useRef<any>(null);
  const [isShowPrev, setIsShowPrev] = useState(false);
  const [isShowNext, setIsShowNext] = useState(false);

  const products = useMemo(() => {
    const data = [...suggestBuyProducts];
    data.unshift(currentProduct);
    return data;
  }, [suggestBuyProducts, currentProduct]);

  const handlePrev = () => {
    if (swiperRef.current) {
      swiperRef?.current?.slidePrev();
    }
  };

  const handleNext = () => {
    if (swiperRef.current) {
      swiperRef.current?.slideNext();
    }
  };

  return (
    <Container>
      <SwiperElement
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        spaceBetween={0}
        navigation={false}
        mousewheel={{ forceToAxis: true }}
        simulateTouch={true}
        modules={[Mousewheel]}
        pagination={false}
        slidesPerView={'auto'}
        className="mySwiper"
        slidesPerGroup={2}
        grabCursor={true}
        centeredSlides={false}
        onReachEnd={() => {
          if (!isShowNext) return;
          setIsShowNext(false);
          setIsShowPrev(true);
        }}
        onReachBeginning={() => {
          if (!isShowPrev) return;
          setIsShowPrev(false);
          setIsShowNext(true);
        }}
        onSlideChange={(swiper) => {
          if (swiper?.isBeginning || swiper?.isEnd) {
            return;
          }
          if (!swiper?.isBeginning !== isShowPrev) {
            setIsShowPrev((prev) => !prev);
          }
          if (!swiper?.isEnd !== isShowNext) {
            setIsShowNext((prev) => !prev);
          }
        }}
        onInit={(swiper) => {
          if (!swiper?.isBeginning !== isShowPrev) {
            setIsShowPrev((prev) => !prev);
          }
          if (!swiper?.isEnd !== isShowNext) {
            setIsShowNext((prev) => !prev);
          }
        }}
      >
        {products?.map((product: Product, index: number) => {
          const isFirstChild = index === 0;
          const isLastChild = index === products.length - 1;
          const isSelected = product?.id === products[0]?.id;
          const isPrevSelected = products[index + 1]?.id === products[0]?.id;
          return (
            <SwiperSlideElement key={product?.id || index}>
              {isFirstChild ? (
                <SuggestBuyByBoxListItem
                  product={product}
                  isFirstChild={isFirstChild}
                  isLastChild={isLastChild}
                  isSelected={isSelected}
                  isPrevSelected={isPrevSelected}
                />
              ) : (
                <Link
                  to={`/products/${generateProductSlug(product?.name, product?.supplierInternalCode, lang)}`}
                  onClick={() => {
                    sendGA4Event(EventName.PDP_CLICK_SUGGEST_BOX, {
                      from_product_id: currentProduct.id,
                      to_product_id: product.id,
                    });
                  }}
                >
                  <SuggestBuyByBoxListItem
                    product={product}
                    isFirstChild={isFirstChild}
                    isLastChild={isLastChild}
                    isSelected={isSelected}
                    isPrevSelected={isPrevSelected}
                  />
                </Link>
              )}
            </SwiperSlideElement>
          );
        })}
      </SwiperElement>
      {isShowPrev && (
        <LeftIconContainer onClick={handlePrev}>
          <SVGIcon src={chevronLeftIcon} renderAsImg={false} />
        </LeftIconContainer>
      )}

      {isShowNext && (
        <RightIconContainer onClick={handleNext}>
          <SVGIcon src={chevronRightIcon} renderAsImg={false} />
        </RightIconContainer>
      )}
    </Container>
  );
};

export default SuggestBuyByBoxList;
